/**
 * ═══════════════════════════════════════════════════════════════
 * WASP Frontend v2.0 - Export Report Button Component
 * ═══════════════════════════════════════════════════════════════
 * 
 * @file components/ExportReportButton.tsx
 * @version 1.0.0
 * @description Botón para exportar reportes de métricas en CSV/PDF
 * @created 2025-10-30 - Fase 2 Session 1
 * 
 * FEATURES:
 * - Export a CSV (métricas y conversaciones)
 * - Modal con opciones de configuración
 * - Selector de datos a incluir
 * - Nombre de archivo personalizable
 * - Descarga automática del archivo
 * - Indicador de progreso
 * - Formato de datos profesional
 */

import React, { useState } from 'react';
import { Button, Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload,
  faFileExport,
  faCheckCircle,
  faFileCsv
} from '@fortawesome/free-solid-svg-icons';

// Types
import {
  ExportReportButtonProps,
  ExportConfig,
  ExportFormat,
  PerformanceMetrics,
  ConversationItem
} from '../types/performance_types';

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

/**
 * Convierte datos a formato CSV
 */
const convertToCSV = (data: any[], headers: string[]): string => {
  const rows: string[] = [];
  
  // Header row
  rows.push(headers.join(','));
  
  // Data rows
  data.forEach(row => {
    const values = headers.map(header => {
      const value = row[header];
      
      // Escapar comillas y comas
      if (typeof value === 'string') {
        return `"${value.replace(/"/g, '""')}"`;
      }
      
      return value !== null && value !== undefined ? value : '';
    });
    
    rows.push(values.join(','));
  });
  
  return rows.join('\n');
};

/**
 * Descarga un archivo
 */
const downloadFile = (content: string, filename: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Genera nombre de archivo con timestamp
 */
const generateFilename = (baseName: string, extension: string): string => {
  const now = new Date();
  const timestamp = now.toISOString().split('T')[0].replace(/-/g, '');
  return `${baseName}_${timestamp}.${extension}`;
};

/**
 * Formatea métricas para CSV
 */
const formatMetricsForCSV = (metrics: PerformanceMetrics): any[] => {
  return [
    { Métrica: 'Total Enviados', Valor: metrics.totalSent },
    { Métrica: 'Total Recibidos', Valor: metrics.totalReceived },
    { Métrica: 'Mensajes Entregados', Valor: metrics.delivered },
    { Métrica: 'Mensajes Leídos', Valor: metrics.read },
    { Métrica: 'Mensajes Fallidos', Valor: metrics.failed },
    { Métrica: 'Mensajes Pendientes', Valor: metrics.pending },
    { Métrica: 'Tasa de Entrega (%)', Valor: metrics.deliveryRate.toFixed(1) },
    { Métrica: 'Tasa de Lectura (%)', Valor: metrics.readRate.toFixed(1) },
    { Métrica: 'Tasa de Respuesta (%)', Valor: metrics.responseRate.toFixed(1) },
    { Métrica: 'Tiempo Promedio Respuesta (min)', Valor: Math.round(metrics.avgResponseTime) },
    { Métrica: 'Conversaciones Activas', Valor: metrics.activeConversations },
    { Métrica: 'Conversaciones Totales', Valor: metrics.totalConversations },
    { Métrica: 'Período', Valor: metrics.timeRange },
    { Métrica: 'Última Actualización', Valor: metrics.lastUpdated.toLocaleString('es-CL') }
  ];
};

/**
 * Formatea conversaciones para CSV
 */
const formatConversationsForCSV = (conversations: ConversationItem[]): any[] => {
  return conversations.map(conv => ({
    'Nombre': conv.contactName,
    'Teléfono': conv.phoneNumber,
    'Estado': conv.status,
    'Mensajes': conv.messageCount,
    'No Leídos': conv.unreadCount,
    'Último Mensaje': conv.lastMessage,
    'Fecha': conv.lastMessageTime.toLocaleString('es-CL'),
    'Dirección': conv.lastMessageDirection === 'outgoing' ? 'Enviado' : 'Recibido'
  }));
};

// ═══════════════════════════════════════════════════════════════
// EXPORT REPORT BUTTON COMPONENT
// ═══════════════════════════════════════════════════════════════

const ExportReportButton: React.FC<ExportReportButtonProps> = ({
  metrics,
  conversations = [],
  onExport,
  exporting = false,
  showAdvancedOptions = true
}) => {

  // ─────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────

  const [showModal, setShowModal] = useState<boolean>(false);
  const [config, setConfig] = useState<ExportConfig>({
    format: ExportFormat.CSV,
    includeMetrics: true,
    includeConversations: true,
    includeCharts: false,
    dateRange: metrics.timeRange,
    filename: 'performance_report'
  });
  const [success, setSuccess] = useState<boolean>(false);

  // ─────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────

  /**
   * Abre el modal de configuración
   */
  const handleOpenModal = () => {
    setShowModal(true);
    setSuccess(false);
  };

  /**
   * Cierra el modal
   */
  const handleCloseModal = () => {
    setShowModal(false);
    setSuccess(false);
  };

  /**
   * Maneja cambios en la configuración
   */
  const handleConfigChange = (field: keyof ExportConfig, value: any) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Ejecuta la exportación
   */
  const handleExport = async () => {
    try {
      // Callback al padre
      if (onExport) {
        onExport(config);
      }

      // Generar contenido según formato
      if (config.format === ExportFormat.CSV) {
        let csvContent = '';

        // Agregar métricas si está seleccionado
        if (config.includeMetrics) {
          csvContent += '=== MÉTRICAS DE PERFORMANCE ===\n\n';
          const metricsData = formatMetricsForCSV(metrics);
          const metricsCSV = convertToCSV(metricsData, ['Métrica', 'Valor']);
          csvContent += metricsCSV + '\n\n\n';
        }

        // Agregar conversaciones si está seleccionado
        if (config.includeConversations && conversations.length > 0) {
          csvContent += '=== CONVERSACIONES RECIENTES ===\n\n';
          const conversationsData = formatConversationsForCSV(conversations);
          const conversationsCSV = convertToCSV(conversationsData, [
            'Nombre',
            'Teléfono',
            'Estado',
            'Mensajes',
            'No Leídos',
            'Último Mensaje',
            'Fecha',
            'Dirección'
          ]);
          csvContent += conversationsCSV + '\n';
        }

        // Descargar archivo
        const filename = generateFilename(
          config.filename || 'performance_report',
          'csv'
        );
        
        downloadFile(csvContent, filename, 'text/csv;charset=utf-8;');
      }

      // Mostrar éxito
      setSuccess(true);

      // Cerrar modal después de 2 segundos
      setTimeout(() => {
        handleCloseModal();
      }, 2000);

    } catch (error) {
      console.error('[ExportReportButton] Error exporting:', error);
      alert('Error al exportar el reporte. Por favor intenta de nuevo.');
    }
  };

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────

  return (
    <>
      {/* Botón principal */}
      <Button
        variant="primary"
        onClick={handleOpenModal}
        disabled={exporting}
        style={{
          borderRadius: '8px',
          padding: '0.5rem 1rem',
          fontWeight: '600'
        }}>
        {exporting ? (
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            Exportando...
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Exportar Reporte
          </>
        )}
      </Button>

      {/* Modal de configuración */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal}
        centered
        backdrop="static">
        <Modal.Header 
          closeButton
          style={{
            backgroundColor: 'var(--bs-body-bg)',
            borderBottom: '1px solid var(--bs-border-color)'
          }}>
          <Modal.Title>
            <FontAwesomeIcon icon={faFileExport} className="me-2" style={{ color: '#0d6efd' }} />
            Exportar Reporte
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ backgroundColor: 'var(--bs-body-bg)' }}>
          
          {/* Mensaje de éxito */}
          {success && (
            <Alert variant="success" className="mb-3">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              Reporte exportado exitosamente
            </Alert>
          )}

          {/* Formato */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: '600' }}>
              Formato de Exportación
            </Form.Label>
            <div className="d-flex gap-2">
              <Button
                variant={config.format === ExportFormat.CSV ? 'primary' : 'outline-primary'}
                onClick={() => handleConfigChange('format', ExportFormat.CSV)}
                style={{ flex: 1 }}>
                <FontAwesomeIcon icon={faFileCsv} className="me-2" />
                CSV
              </Button>
              <Button
                variant={config.format === ExportFormat.PDF ? 'primary' : 'outline-primary'}
                onClick={() => handleConfigChange('format', ExportFormat.PDF)}
                disabled
                style={{ flex: 1 }}>
                PDF (Próximamente)
              </Button>
            </div>
          </Form.Group>

          {/* Opciones de contenido */}
          {showAdvancedOptions && (
            <>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600' }}>
                  Contenido a Incluir
                </Form.Label>
                
                <Form.Check
                  type="checkbox"
                  id="include-metrics"
                  label="Incluir métricas de performance"
                  checked={config.includeMetrics}
                  onChange={(e) => handleConfigChange('includeMetrics', e.target.checked)}
                  className="mb-2"
                />
                
                <Form.Check
                  type="checkbox"
                  id="include-conversations"
                  label={`Incluir conversaciones (${conversations.length})`}
                  checked={config.includeConversations}
                  onChange={(e) => handleConfigChange('includeConversations', e.target.checked)}
                  disabled={conversations.length === 0}
                />
              </Form.Group>

              {/* Nombre del archivo */}
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600' }}>
                  Nombre del Archivo
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="performance_report"
                  value={config.filename}
                  onChange={(e) => handleConfigChange('filename', e.target.value)}
                  style={{
                    backgroundColor: 'var(--bs-body-bg)',
                    border: '1px solid var(--bs-border-color)'
                  }}
                />
                <Form.Text className="text-muted">
                  El archivo se guardará como: {generateFilename(config.filename || 'report', 'csv')}
                </Form.Text>
              </Form.Group>
            </>
          )}

          {/* Info del reporte */}
          <div 
            style={{
              backgroundColor: 'var(--bs-light)',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid var(--bs-border-color)'
            }}>
            <small style={{ fontWeight: '600', color: 'var(--bs-body-color)' }}>
              Resumen del Reporte:
            </small>
            <ul className="mb-0 mt-2" style={{ fontSize: '0.875rem' }}>
              {config.includeMetrics && (
                <li>14 métricas de performance</li>
              )}
              {config.includeConversations && conversations.length > 0 && (
                <li>{conversations.length} conversaciones</li>
              )}
              <li>Período: {metrics.timeRange}</li>
              <li>Formato: {config.format.toUpperCase()}</li>
            </ul>
          </div>

        </Modal.Body>

        <Modal.Footer 
          style={{
            backgroundColor: 'var(--bs-body-bg)',
            borderTop: '1px solid var(--bs-border-color)'
          }}>
          <Button 
            variant="outline-secondary" 
            onClick={handleCloseModal}
            disabled={success}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleExport}
            disabled={success || (!config.includeMetrics && !config.includeConversations)}>
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Exportar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ExportReportButton;